import { supabase } from '../../config/supabase';
import {
  Activity,
  RawActivity,
  ActivityFilters,
  ActivityResponse,
  ACTIVITY_PERIOD_DAYS
} from '../../types/activity';
import logger from '../../utils/logger';
import { authService } from './auth.service';

/**
 * Convertit un objet activity du format Supabase vers le format de l'application
 * @param data Données de l'activité au format Supabase
 * @returns Activity au format de l'application
 */
function toAppFormat(data: RawActivity): Activity {
  return {
    id: data.id,
    occurredAt: new Date(data.occurred_at),
    agencyId: data.agency_id,
    userId: data.user_id,
    userEmail: data.user_email,
    userName: data.user_name,
    activityName: data.activity_name,
    targetType: data.target_type,
    targetId: data.target_id,
    result: data.result,
    notes: data.notes,
    geoLat: data.geo_lat,
    geoLng: data.geo_lng,
    createdAt: new Date(data.created_at)
  };
}

/**
 * Service pour gérer les activités
 */
export const activitiesService = {
  /**
   * Récupère les activités avec filtres et pagination
   * @param filters Filtres à appliquer
   * @returns Liste paginée des activités
   */
  async getActivities(filters: ActivityFilters): Promise<ActivityResponse> {
    try {
      const { user, error: authError } = await authService.getCurrentUser();
      if (authError || !user) {
        throw new Error('Utilisateur non authentifié');
      }

      // Récupérer le profil utilisateur pour vérifier le rôle
      const { data: profile, error: profileError } = await supabase
        .from('users')
        .select('role, agency_id')
        .eq('id', user.id)
        .single();

      if (profileError) {
        logger.error('Erreur lors de la récupération du profil utilisateur:', profileError);
        throw profileError;
      }

      // Déterminer le user_id à filtrer
      // Si admin et pas de userId spécifié, on récupère toutes les activités de l'agence
      // Sinon, on récupère uniquement les activités de l'utilisateur
      const userIdFilter = profile.role === 'admin' && !filters.userId 
        ? null 
        : filters.userId || user.id;

      // Calculer le nombre de jours pour le filtre de période
      const periodDays = filters.period 
        ? ACTIVITY_PERIOD_DAYS[filters.period] 
        : null;

      // Appeler la fonction SQL get_user_activities
      logger.debug('Appel get_user_activities avec params:', {
        p_agency_id: filters.agencyId,
        p_user_id: userIdFilter,
        p_limit: filters.limit || 50,
        p_offset: filters.offset || 0,
        p_period_days: periodDays
      });

      const { data, error } = await supabase.rpc('get_user_activities', {
        p_agency_id: filters.agencyId,
        p_user_id: userIdFilter,
        p_limit: filters.limit || 50,
        p_offset: filters.offset || 0,
        p_period_days: periodDays
      });

      if (error) {
        logger.error('Erreur lors de la récupération des activités:', {
          error,
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        throw error;
      }

      const activities = (data || []).map(toAppFormat);

      // Pour déterminer s'il y a plus de résultats, on vérifie si on a atteint la limite
      const hasMore = activities.length === (filters.limit || 50);

      return {
        activities,
        total: activities.length,
        hasMore
      };
    } catch (error) {
      logger.error('Erreur dans getActivities:', error);
      throw error;
    }
  },

  /**
   * Récupère une activité par son ID
   * @param id ID de l'activité
   * @returns L'activité ou null si non trouvée
   */
  async getActivityById(id: string): Promise<Activity | null> {
    try {
      const { data, error } = await supabase
        .from('activities')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        logger.error('Erreur lors de la récupération de l\'activité:', error);
        throw error;
      }

      // Récupérer les infos utilisateur séparément
      const { data: userData } = await supabase
        .from('users')
        .select('name, first_name')
        .eq('id', data.user_id)
        .single();

      const { data: authData } = await supabase.auth.admin.getUserById(data.user_id);

      const activityData: RawActivity = {
        ...data,
        user_email: authData.user?.email,
        user_name: userData ? `${userData.name || ''} ${userData.first_name || ''}`.trim() : undefined
      };

      return toAppFormat(activityData);
    } catch (error) {
      logger.error('Erreur dans getActivityById:', error);
      throw error;
    }
  },

  /**
   * Récupère les activités liées à une cible spécifique
   * @param targetType Type de la cible
   * @param targetId ID de la cible
   * @param limit Nombre maximum de résultats
   * @returns Liste des activités
   */
  async getActivitiesByTarget(
    targetType: 'marker' | 'notification' | 'contact',
    targetId: string,
    limit: number = 20
  ): Promise<Activity[]> {
    try {
      const { data, error } = await supabase
        .from('activities')
        .select('*')
        .eq('target_type', targetType)
        .eq('target_id', targetId)
        .order('occurred_at', { ascending: false })
        .limit(limit);

      if (error) {
        logger.error('Erreur lors de la récupération des activités par cible:', error);
        throw error;
      }

      // Pour simplifier, on retourne les activités sans les infos utilisateur détaillées
      // La fonction RPC get_user_activities est préférée pour avoir ces infos
      return (data || []).map((item: any) => toAppFormat({
        ...item,
        user_email: undefined,
        user_name: undefined
      }));
    } catch (error) {
      logger.error('Erreur dans getActivitiesByTarget:', error);
      throw error;
    }
  },

  /**
   * Compte le nombre total d'activités pour un utilisateur
   * @param agencyId ID de l'agence
   * @param userId ID de l'utilisateur (optionnel, pour admin)
   * @returns Nombre total d'activités
   */
  async countActivities(agencyId: string, userId?: string): Promise<number> {
    try {
      const { user, error: authError } = await authService.getCurrentUser();
      if (authError || !user) {
        throw new Error('Utilisateur non authentifié');
      }

      let query = supabase
        .from('activities')
        .select('*', { count: 'exact', head: true })
        .eq('agency_id', agencyId);

      // Si userId est spécifié, filtrer par utilisateur
      if (userId) {
        query = query.eq('user_id', userId);
      }

      const { count, error } = await query;

      if (error) {
        logger.error('Erreur lors du comptage des activités:', error);
        throw error;
      }

      return count || 0;
    } catch (error) {
      logger.error('Erreur dans countActivities:', error);
      throw error;
    }
  }
};
